import {
  Alert,
  AlertType,
  Button,
  ButtonVariant,
  IconButton,
  IconColor,
  IconName,
  Text,
  TextSize,
  makeStyles
} from '@focaldata/cin-ui-components'
import { useState } from 'react'
import { useEvictMarketAudienceTextCache } from '../../../../hooks/audience/useEvictMarketAudienceTextCache'
import { useFieldwork } from '../../../../hooks/useFieldwork'
import { useIsAudienceReadOnly } from '../../../../hooks/useIsAudienceReadOnly'
import { useProjectId } from '../../../../hooks/useProjectId'
import { AudienceContextProvider } from '../../../../modules/Audience/Audience.context'
import AudienceCriteriaConfigurator from '../../../../modules/Audience/AudienceCriteriaConfigurator/AudienceCriteriaConfigurator'
import { AudienceSummaryContainer } from '../../../../modules/Audience/AudienceSummary'
import { useGetProjectByProjectId } from '../../../../modules/Project/Project.hooks'
import { QuestionnaireContextProvider } from '../../../../modules/Questionnaire/Questionnaire.context'
import { MarketTabsLayout } from '../MarketTabsLayout'
import { ProjectFlowBreadcrumb } from '../ProjectFlowBreadcrumb'
import { getMarkets } from '../markets.utils'
import { MarketSettingsDrawer } from './MarketSettingsDrawer'
import { useWeightedPresetLock } from './useWeightedPresetLock'

const useStyles = makeStyles()({
  loading: { padding: '24px 0', color: '#6B7280' },
  // The settings button floats in the top-right of the editor so it doesn't
  // add a row that pushes the audience content down.
  editor: { position: 'relative' },
  settings: { position: 'absolute', top: -10, right: -10, zIndex: 1 },
  // Clear the floating settings gear so the banner text doesn't run under it.
  lockBanner: { marginBottom: 16, marginRight: 40 },
  lockBannerAction: { marginTop: 8 }
})

/**
 * The targeting/screening view for the selected market, in one of three
 * states:
 * - read-only (market has left Draft): audience summary only;
 * - weighted-locked (a weighted preset audience is applied): audience summary
 *   with an explanatory banner, but the settings gear and Market settings
 *   drawer stay available so the audience itself can still be changed;
 * - editable: the full criteria editor.
 * The settings gear and drawer are shown in all three: a launched market's
 * audience is fixed, but its audience label can still be edited (the drawer's
 * own fields each gate themselves on useIsAudienceReadOnly).
 * The audience criteria read the fieldwork locale cache-only, so the
 * fieldwork is fetched here first — otherwise a direct page load (cold cache,
 * no prior tab navigation) throws "Locale not found". Both context providers
 * are required: the editor runs questionnaire validation, which needs
 * QuestionnaireContext. Exported for tests.
 */
export const MarketAudienceEditor = () => {
  const { classes } = useStyles()
  const { fieldwork, loading } = useFieldwork()
  const { isReadOnly } = useIsAudienceReadOnly()
  const {
    locked: weightedLocked,
    resolving: lockResolving,
    unresolved: lockUnresolved,
    presetName
  } = useWeightedPresetLock()
  const [settingsOpen, setSettingsOpen] = useState(false)

  useEvictMarketAudienceTextCache()

  if (loading || !fieldwork) {
    return (
      <div className={classes.loading}>
        <Text size={TextSize.ms}>Loading…</Text>
      </div>
    )
  }

  return (
    <AudienceContextProvider>
      <QuestionnaireContextProvider>
        <div className={classes.editor}>
          <div className={classes.settings}>
            <IconButton
              ariaLabel="Market settings"
              iconName={IconName.Settings}
              iconColor={IconColor.Background}
              onClick={() => setSettingsOpen(true)}
            />
          </div>
          {isReadOnly ? (
            <AudienceSummaryContainer showTitle />
          ) : lockResolving ? (
            // The lock fails closed while the preset lists resolve (cold
            // cache), but the preset may turn out unweighted — showing the
            // weighted banner (or the editor) before knowing would be wrong
            // either way. Checked after isReadOnly so a read-only market's
            // summary is never blocked behind a preset fetch it doesn't use.
            <div className={classes.loading}>
              <Text size={TextSize.ms}>Loading…</Text>
            </div>
          ) : weightedLocked ? (
            <>
              {/* Anchor the copy on "weighted preset", never "live" — a locked
                  draft market must not read as a launched one. The inline
                  action co-locates the fix path with the explanation (the gear
                  alone is easy to miss). An unresolved preset (fetch failure /
                  its locale removed) gets honest copy: we can't verify it, so
                  we don't claim it is weighted. */}
              <div className={classes.lockBanner}>
                <Alert severity={AlertType.INFO}>
                  {lockUnresolved
                    ? `The preset audience${
                        presetName ? ` “${presetName}”` : ''
                      } applied to this market couldn’t be verified, so its targeting and screening are locked. To change the audience, switch to Custom or another preset in Market settings.`
                    : `This market uses the weighted preset audience${
                        presetName ? ` “${presetName}”` : ''
                      }. Weighted audiences automatically weight the results when fieldwork is complete, so their targeting and screening can’t be edited. To change the audience, switch to Custom or an unweighted preset in Market settings.`}
                  <div className={classes.lockBannerAction}>
                    <Button
                      variant={ButtonVariant.SecondaryOutlined}
                      onClick={() => setSettingsOpen(true)}
                    >
                      Open market settings
                    </Button>
                  </div>
                </Alert>
              </div>
              <AudienceSummaryContainer
                showTitle
                // Presets always carry members, but if the summary is ever
                // transiently empty its default copy says "Edit targeting
                // and screening…" — the one thing a locked market can't do.
                emptySubtitle="This market’s audience is defined by its applied preset."
                emptyDescription="The preset’s targeting and screening will appear here."
              />
            </>
          ) : (
            <AudienceCriteriaConfigurator />
          )}
          <MarketSettingsDrawer
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
          />
        </div>
      </QuestionnaireContextProvider>
    </AudienceContextProvider>
  )
}

export const AudiencesPage = () => {
  const projectId = useProjectId()
  const { project } = useGetProjectByProjectId()

  return (
    <MarketTabsLayout
      breadcrumb={<ProjectFlowBreadcrumb activeStep="Audiences" />}
      title="Audiences"
      marketBasePath={`/project/${projectId}/audiences`}
      markets={getMarkets(project)}
      emptyText="Add a market to configure its audience."
      renderEditor={() => <MarketAudienceEditor />}
    />
  )
}
